import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Crown, Gamepad2, MoreVertical, UserMinus, Shield, Undo2, X, AlertTriangle, Star, Users } from 'lucide-react';
import { api, parseApiError, type ClanMember, type ClanScopedRole } from '../lib/api';
import { cn } from '../lib/cn';
import { MakeCaptainModal } from './MakeCaptainModal';
import { teamBadgeLabel } from '../lib/roles';
import { canAssignToTeam, canUnassignFromTeam } from '../lib/captainAccess';

type Toast = { kind: 'removed' | 'role' | 'error'; msg: string; undo?: () => void } | null;

// Same shortId idiom as OrgMembersPanel/OrgProposalsPanel/OrgClansPanel/
// CompetitionMembersPanel — never render a raw Discord snowflake.
function shortId(discordId: string): string {
  return `…${discordId.slice(-6)}`;
}

export function ClanMemberRow({
  member,
  clanName,
  canManage,
  canPromoteToLeader = false,
  canDelegate = false,
  canAssignTeam = false,
  canManageTeamsFully = false,
  viewerCaptainTeams = [],
  onToast,
}: {
  member: ClanMember;
  clanName: string;
  canManage: boolean;
  canPromoteToLeader?: boolean;
  canDelegate?: boolean;
  canAssignTeam?: boolean;
  // Own-team seat/bench assignment (2026-07-21): canAssignTeam above just
  // gates whether the "Set team" menu entry/sheet opens at all; these two
  // narrow WHICH destinations the sheet offers once open. canManageTeamsFully
  // = the existing leader/co-admin/platform-admin boolean (full picker,
  // unchanged); viewerCaptainTeams scopes a captain to their own team(s) —
  // mirrors ClanTeamsBoard's identical own-team gating via the same
  // canAssignToTeam/canUnassignFromTeam helpers (captainAccess.ts), which
  // mirror the backend's PATCH .../members/:id/team overlay exactly.
  canManageTeamsFully?: boolean;
  viewerCaptainTeams?: number[];
  onToast: (t: Toast) => void;
}) {
  const qc = useQueryClient();
  // All state hooks must be declared before any early return
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [captainModalOpen, setCaptainModalOpen] = useState(false);
  const [teamPickerOpen, setTeamPickerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const roleMut = useMutation({
    mutationFn: (scopedRole: ClanScopedRole) =>
      api.updateClanMemberRole(clanName, member.discordId, scopedRole),
    onSuccess: (_data, scopedRole) => {
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      const label = scopedRole === 'CLAN_LEADER' ? 'promoted to leader' : 'set to member';
      onToast({ kind: 'role', msg: `${member.gamertag ?? member.displayName ?? shortId(member.discordId)} ${label}.` });
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const removeMut = useMutation({
    mutationFn: () => api.removeClanMember(clanName, member.discordId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      onToast({ kind: 'removed', msg: `${member.gamertag ?? member.displayName ?? shortId(member.discordId)} removed from ${clanName}.` });
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const teamMut = useMutation({
    mutationFn: (teamNo: number | null) =>
      api.setMemberTeam(clanName, member.discordId, teamNo),
    onSuccess: (_data, teamNo) => {
      qc.invalidateQueries({ queryKey: ['clan', clanName] });
      const label = teamNo ? `Team ${teamNo}` : 'bench';
      onToast({ kind: 'role', msg: `${member.gamertag ?? member.displayName ?? shortId(member.discordId)} moved to ${label}.` });
      setTeamPickerOpen(false);
    },
    onError: (e: Error) => onToast({ kind: 'error', msg: parseApiError(e).message }),
  });

  const isLeader = member.role === 'LEADER';
  const busy = roleMut.isPending || removeMut.isPending || teamMut.isPending;
  const teamLabel = teamBadgeLabel(member.teamNo);

  // Own-team seat/bench assignment: which destinations the "Set Team" sheet
  // offers. canManageTeamsFully sees everything (unchanged); a captain
  // (canManageTeamsFully=false) sees Bench only if THIS member's current
  // team is one they captain, and only their own team(s) as a destination —
  // never another team, never "move between two other teams".
  const canBenchThisMember = canUnassignFromTeam(canManageTeamsFully, viewerCaptainTeams, member.teamNo);
  const assignableTeamNos = Array.from({ length: 10 }, (_, i) => i + 1)
    .filter(n => canAssignToTeam(canManageTeamsFully, viewerCaptainTeams, n));

  return (
    <>
      <div
        className={cn(
          'group relative flex items-center justify-between border border-iron-800/60 bg-iron-950 px-4 py-3 transition-colors',
          busy && 'opacity-60',
          !busy && 'hover:border-forge-900/60 hover:bg-iron-950'
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center border',
              isLeader ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-iron-700/50 bg-iron-950'
            )}
          >
            {isLeader ? (
              <Crown className="h-4 w-4 text-emerald-300" strokeWidth={2.5} />
            ) : (
              <Gamepad2 className="h-4 w-4 text-iron-400" strokeWidth={2} />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-display text-sm font-black uppercase tracking-wide text-white">
              {member.gamertag ?? member.displayName ?? <span className="italic text-iron-500">No name</span>}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {teamLabel && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.18em] ring-1 ring-inset bg-forge-500/10 text-forge-300 ring-forge-500/30">
              <Users className="h-2.5 w-2.5" strokeWidth={2.5} />
              {teamLabel}
            </span>
          )}
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 font-display text-[9px] font-black uppercase tracking-[0.18em] ring-1 ring-inset',
              isLeader
                ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30'
                : 'bg-iron-900 text-iron-300 ring-iron-600/40'
            )}
          >
            {isLeader ? 'Leader' : 'Member'}
          </span>

          {canManage && (
            <div ref={menuRef} className="relative">
              <button
                onClick={() => setMenuOpen(o => !o)}
                disabled={busy}
                className="flex h-7 w-7 items-center justify-center border border-iron-800 text-iron-400 transition-colors hover:border-forge-500 hover:text-forge-200 disabled:opacity-50"
                aria-label="Member actions"
              >
                <MoreVertical className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full z-40 mt-1 w-52 border border-forge-900/60 bg-iron-950 shadow-forged">
                  <div className="border-b border-forge-900/40 px-3 py-2">
                    <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                      Actions
                    </p>
                  </div>
                  <div className="py-1">
                    {isLeader && canPromoteToLeader ? (
                      <button
                        onClick={() => { setMenuOpen(false); roleMut.mutate('CLAN_MEMBER'); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                      >
                        <Shield className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                        Demote to Member
                      </button>
                    ) : !isLeader && canPromoteToLeader ? (
                      <button
                        onClick={() => { setMenuOpen(false); roleMut.mutate('CLAN_LEADER'); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                      >
                        <Crown className="h-3 w-3 text-emerald-400" strokeWidth={2.5} />
                        Promote to Leader
                      </button>
                    ) : null}
                    {canDelegate && !isLeader && (
                      <button
                        onClick={() => { setMenuOpen(false); setCaptainModalOpen(true); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                      >
                        <Star className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                        Make Captain
                      </button>
                    )}
                    {canAssignTeam && (
                      <button
                        onClick={() => { setMenuOpen(false); setTeamPickerOpen(true); }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-iron-200 transition-colors hover:bg-iron-950 hover:text-white"
                      >
                        <Users className="h-3 w-3 text-forge-400" strokeWidth={2.5} />
                        Set team
                      </button>
                    )}
                    <button
                      onClick={() => { setMenuOpen(false); setConfirmRemove(true); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-300 transition-colors hover:bg-red-950/40 hover:text-red-200"
                    >
                      <UserMinus className="h-3 w-3" strokeWidth={2.5} />
                      Remove from clan
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Captain grant modal — preset the member's own team when they have
          one (Lisa, 2026-07-21: captaincy is per-team, no "Clan-wide" option
          any more); a benched member (teamNo null) opens the modal with the
          picker empty and must choose a team before the modal will submit. */}
      {captainModalOpen && (
        <MakeCaptainModal
          member={member}
          clanName={clanName}
          presetTeamNo={member.teamNo ?? undefined}
          onClose={() => setCaptainModalOpen(false)}
          onToast={onToast}
        />
      )}

      {/* Team picker sheet */}
      {teamPickerOpen && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            onClick={() => setTeamPickerOpen(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-forge-500/40 bg-iron-950 p-5 shadow-forged sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-1/2 sm:w-full sm:max-w-xs sm:-translate-y-1/2 sm:border">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-[9px] font-black uppercase tracking-[0.22em] text-forge-400">
                Set Team — {member.gamertag ?? member.displayName ?? shortId(member.discordId)}
              </p>
              <button
                onClick={() => setTeamPickerOpen(false)}
                className="flex h-6 w-6 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-iron-600 hover:text-iron-300"
                aria-label="Close"
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {/* Own-team seat/bench assignment (2026-07-21): a captain only
                  sees Bench when THIS member's current team is one they
                  captain (canUnassignFromTeam) — a leader/admin sees it
                  unconditionally, unchanged. */}
              {canBenchThisMember && (
                <button
                  onClick={() => teamMut.mutate(null)}
                  disabled={teamMut.isPending}
                  className={cn(
                    'col-span-4 border px-3 py-2 font-display text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-50',
                    member.teamNo == null
                      ? 'border-iron-600 bg-iron-900 text-white'
                      : 'border-iron-800 bg-iron-950 text-iron-300 hover:border-iron-600 hover:bg-iron-900 hover:text-white'
                  )}
                >
                  Bench
                </button>
              )}
              {/* Own-team seat/bench assignment: a captain sees ONLY their
                  own team(s) as a destination (canAssignToTeam, destination-
                  only — mirrors the backend exactly, no source restriction);
                  a leader/admin sees all 10, unchanged. */}
              {assignableTeamNos.map(n => (
                <button
                  key={n}
                  onClick={() => teamMut.mutate(n)}
                  disabled={teamMut.isPending}
                  className={cn(
                    'border px-2 py-2 font-display text-[11px] font-black uppercase tracking-wide transition-colors disabled:opacity-50',
                    member.teamNo === n
                      ? 'border-forge-500/60 bg-forge-500/20 text-forge-200'
                      : 'border-iron-800 bg-iron-950 text-iron-300 hover:border-forge-900/60 hover:bg-iron-900 hover:text-white'
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Confirmation sheet */}
      {confirmRemove && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            onClick={() => setConfirmRemove(false)}
          />
          <div className="fixed inset-x-0 bottom-0 z-[80] border-t border-red-500/40 bg-iron-950 p-5 shadow-forged sm:inset-x-auto sm:bottom-auto sm:right-6 sm:top-1/2 sm:w-full sm:max-w-sm sm:-translate-y-1/2 sm:border">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-red-500/40 bg-red-500/10">
                <AlertTriangle className="h-4 w-4 text-red-300" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-black uppercase tracking-wide text-white">
                  Remove {member.gamertag ?? 'this member'}?
                </p>
                <p className="mt-1 text-xs text-iron-400">
                  Their seat becomes a ghost again and can be claimed by someone else.
                </p>
              </div>
              <button
                onClick={() => setConfirmRemove(false)}
                className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-iron-600 hover:text-iron-300"
                aria-label="Cancel"
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => setConfirmRemove(false)}
                className="flex-1 border border-iron-700 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:border-iron-500 hover:bg-iron-900 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmRemove(false); removeMut.mutate(); }}
                className="flex-1 border border-red-500/60 bg-red-950/60 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-red-100 transition-all hover:border-red-400 hover:bg-red-900/70 hover:text-white"
              >
                <UserMinus className="mr-1 inline h-3 w-3" strokeWidth={3} />
                Remove
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Toast host ───────────────────────────────────────────────────────────────

export function ToastHost({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);

  if (!toast) return null;

  const tone =
    toast.kind === 'error'
      ? 'border-red-500/50 bg-red-950/80 text-red-100'
      : toast.kind === 'removed'
      ? 'border-forge-500/40 bg-iron-950 text-iron-100'
      : 'border-emerald-500/40 bg-iron-950 text-iron-100';

  return (
    <div className="fixed bottom-6 right-6 z-[90] max-w-sm">
      <div className={cn('flex items-start gap-3 border px-4 py-3 shadow-forged backdrop-blur-md', tone)}>
        <p className="flex-1 text-sm">{toast.msg}</p>
        {toast.undo && (
          <button
            onClick={() => { toast.undo?.(); onDismiss(); }}
            className="flex items-center gap-1 border border-forge-500/60 bg-iron-950 px-2 py-1 font-display text-[10px] font-black uppercase tracking-wider text-forge-200 transition-colors hover:border-forge-400 hover:text-white"
          >
            <Undo2 className="h-3 w-3" strokeWidth={2.5} />
            Undo
          </button>
        )}
        <button
          onClick={onDismiss}
          className="flex h-6 w-6 items-center justify-center text-iron-500 hover:text-iron-300"
          aria-label="Dismiss"
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
